import * as d3 from 'd3';
import React from 'react';

function adjustLabelPosition(labelData, labelToLabelMinSpacing) {
  return labelData.map((label, idx) => {
    if (idx) {
      const diff = label.x - labelData[idx - 1].x;
      if (diff < labelToLabelMinSpacing && diff >= 0) {
        label.x = labelData[idx - 1].x + labelToLabelMinSpacing;
      }
      if (diff < 0 && Math.abs(diff) < labelToLabelMinSpacing) {
        label.x = labelData[idx - 1].x - labelToLabelMinSpacing;
      }
      // if a label is to the left of a previous label (which only happens when surveys are missing data,
      // blank out the label because it's offering confusing data
      if (diff < 0) {
        label.hideLabel = true;
      }
    }

    if (idx === 0) {
      label = adjustLargeSalaryLabels(label);
    }

    if (idx === labelData.length - 1) {
      const maxViewableSalary = 1000000;
      label.x -= 15;
    }

    return label;
  });
}

function adjustLargeSalaryLabels(label) {
  const maxViewableSalary = 1000000;
  let newSalary = '';
  //extract all numeric values from salary
  for (var i = 0; i < label.val.length; i++) {
    if (!isNaN(label.val[i])) {
      newSalary += label.val[i];
    }
  }
  if (parseInt(newSalary) >= maxViewableSalary) {
    label.x += 15;
  }

  return label;
}

function getLabel(data, labelNames, formatter, key, index, widthMultiplier, paddingOffset) {
  return data[key]
    ? {
        val: formatter(data[key], key),
        name: labelNames[index],
        x: paddingOffset + data['x' + key] * widthMultiplier
      }
    : null;
}

function getMinLabel(data, formatter, paddingOffset) {
  return {
    val: formatter(data.scaleMin, ''),
    name: 'min',
    x: paddingOffset,
    hideLabel: true
  };
}

function getMaxLabel(data, formatter, width, paddingOffset) {
  return {
    val: formatter(data.scaleMax, ''),
    name: 'max',
    x: width - paddingOffset,
    hideLabel: true
  };
}

function getContainedSegment(val, lineData) {
  for (let i = 1; i < lineData.length; i++) {
    const line = lineData[i],
      compareValue = lineData[i + 1] ? lineData[i + 1].x : line.x + line.width;
    if (val > line.x && val <= compareValue) return i;
  }
  return 1;
}

export default function drawRangeChart(chartData, scatterClickHandler, currencyFormatter, genderToggle) {
  if (!chartData.data) {
    return false;
  }

  let {
    width,
    height, // eslint-disable-line prefer-const
    barHeight, // eslint-disable-line prefer-const
    labelToBarSpacingBottom: labelSpaceBottom, // eslint-disable-line prefer-const
    labelToBarSpacingTop: labelSpaceTop, // eslint-disable-line prefer-const
    circleRadius, // eslint-disable-line prefer-const
    circleStrokeWidth, // eslint-disable-line prefer-const
    svgId, // eslint-disable-line prefer-const
    containerId, // eslint-disable-line prefer-const
    toolTipWidth,
    toolTipToBarSpacing,
    mode,
    scatterHoverHandler,
    scatterHoverHandlerOut
  } = chartData;

  const { highLightScatterPointIds: highlightIds, type, reportMode } = chartData;

  let animateDuration = 1000;
  let textAnimationDuration = 300;
  if (chartData.animate === false) {
    animateDuration = 0;
    textAnimationDuration = 0;
  }

  mode = reportMode || mode;
  const topLabelNames = ['10th', '25th', '50th', '75th', '90th'];

  toolTipWidth = toolTipWidth || 130;
  toolTipToBarSpacing = toolTipToBarSpacing || 10;
  circleRadius = circleRadius || 7;
  const paddingMultiplier = 0.05,
    paddingOffset = width * paddingMultiplier,
    calculatedWidth = width - 2 * paddingOffset,
    widthMultiplier = calculatedWidth / width;
  circleStrokeWidth = circleStrokeWidth || 2;
  const barY = height / 2 - barHeight / 2;
  if (!chartData.scale) {
    // Find scale by using given data
    const valueObject = {};
    topLabelNames.forEach(name => {
      valueObject[name] = chartData.data[name];
    });
    const values = Object.values(valueObject).filter(val => !isNaN(val) && val !== null);
    const maxBarValue = Math.max(...values);
    const minBarValue = Math.min(...values);
    chartData?.scatterData?.forEach(point => {
      if (point.y > maxBarValue * 1.5) point.y = maxBarValue * 1.5;
      if (point.y < minBarValue * 0.5) point.y = minBarValue * 0.5;
      values.push(point.y);
    });
    chartData.scaleMax = Math.max(...values);
    chartData.scaleMin = Math.min(...values);
  } else {
    chartData.scaleMax = chartData.scale.max;
    chartData.scaleMin = chartData.scale.min;
  }
  chartData.data.lineStartx = 0;

  const pixPerValRatio = width / (chartData.scaleMax - chartData.scaleMin);
  if (chartData.data['10th'] === chartData.data['25th']) chartData.data['10th'] = null;
  if (chartData.data['90th'] === chartData.data['75th']) chartData.data['90th'] = null;
  Object.keys(chartData.data).forEach(key => {
    chartData.data['x' + key] = (chartData.data[key] - chartData.scaleMin) * pixPerValRatio;
  });

  let labelData = [
    !chartData.scale ? null : getMinLabel(chartData.data, currencyFormatter, paddingOffset),
    getLabel(chartData.data, topLabelNames, currencyFormatter, '10th', 0, widthMultiplier, paddingOffset),
    getLabel(chartData.data, topLabelNames, currencyFormatter, '25th', 1, widthMultiplier, paddingOffset),
    getLabel(chartData.data, topLabelNames, currencyFormatter, '50th', 2, widthMultiplier, paddingOffset),
    getLabel(chartData.data, topLabelNames, currencyFormatter, '75th', 3, widthMultiplier, paddingOffset),
    getLabel(chartData.data, topLabelNames, currencyFormatter, '90th', 4, widthMultiplier, paddingOffset),
    !chartData.scale ? null : getMaxLabel(chartData.data, currencyFormatter, width, paddingOffset)
  ];

  labelData = labelData.filter(label => label !== null);

  labelData = [...labelData, ...labelData];

  let lineData = labelData
    .map((label, idx) => {
      const xVal = label.x;
      if (idx < labelData.length / 2) label.x = xVal + 1.5 * idx;
      if (idx < labelData.length / 2 - 1) {
        return {
          x: xVal + 1.5 * idx,
          width: labelData[idx + 1].x - xVal - 1.5,
          height: 20,
          color: '#005b96',
          y: height / 2 - 10
        };
      }
    })
    .filter(line => line);
  lineData.unshift({
    color: '#c0c0bf',
    height: 1,
    width: width,
    x: 0,
    y: height / 2
  });

  if (type === 'grade') {
    lineData = [];
  }

  const data = lineData;
  //
  // set any widths to 0 if they are negative
  data.forEach(item => {
    if (item.width < 0) item.width = 0;
  });
  const scatterData = chartData.scatterData.map(point => {
    point.x = paddingOffset + widthMultiplier * (point.y - chartData.scaleMin) * pixPerValRatio;
    const spacingOffsetAdjustment = getContainedSegment(point.x, lineData);
    point.x = point.x + 1.5 * spacingOffsetAdjustment - circleStrokeWidth;

    if (highlightIds) point.sortOrder = highlightIds.find(id => id === point.value) ? 1 : 0;

    return point;
  });

  if (highlightIds) scatterData.sort((a, b) => a.sortOrder - b.sortOrder);

  d3.selectAll('#' + svgId + ' > *').remove();
  const svg = d3.select('#' + svgId).attr('height', height);

  const nodes = svg.selectAll('rect').data(data);

  nodes.enter().append('rect');

  nodes
    .attr('width', 0)
    .style({
      fill: (d, i) => d.color
    })
    .attr('x', (d, i) => d.x)
    .attr('y', (d, i) => d.y || barY)
    .transition()
    .duration(animateDuration)
    .attr('width', (d, i) => d.width)
    .attr('height', (d, i) => d.height || chartData.barHeight);

  svg.selectAll('circle').remove();
  const scatterPoints = svg.selectAll('circle').data(scatterData);
  scatterPoints
    .enter()
    .append('circle')
    .classed('pointer', true);
  
  const originalFillMaleColor = 'rgb(15, 178, 246)';
  const originalFillFemaleColor = 'rgb(198, 179, 252)';
  const originalFillNullColor = 'rgb(200, 200, 200)';
  
  const originalStrokeMaleColor = 'rgb(2, 132, 187)';
  const originalStrokeFemaleColor = 'rgb(148, 131, 194)';
  const originalStrokeNullColor = 'rgb(180, 180, 180)';

  scatterPoints
    .attr('r', 0)
    .style('stroke', (d) => {
      if (!genderToggle) return 'black';
      if (d.gender === 'F' || d.gender === 'Female') return originalStrokeFemaleColor;
      if (d.gender === 'M' || d.gender === 'Male') return originalStrokeMaleColor;
      return originalStrokeNullColor; // for null or unspecified gender
    })
    .style('stroke-width', 1)
    .style('fill', (d) => {
      if (!genderToggle) return 'white';
      if (d.gender === 'F' || d.gender === 'Female') return originalFillFemaleColor;
      if (d.gender === 'M' || d.gender === 'Male') return originalFillMaleColor;
      return originalFillNullColor; // for null or unspecified gender
    })
    .attr('cx', (d) => d.x)
    .attr('cy', (d, i) => height / 2)
    .transition()
    .duration(animateDuration)
    .attr('r', circleRadius || 7);

  function mouseoverfn() {
    let labels = svg.selectAll('text');
    //
    // const doAnimation = labels[0].length;
    labels = labels.data(labelData);
    labels.enter().append('text');
    labels
      .style({
        'fill-opacity': 0.0
      })
      .attr('text-anchor', 'middle')
      .attr('x', (d, i) => {
        return d.x;
      })
      .attr('y', (d, i) => {
        if (i < labelData.length / 2) {
          return height / 2 + barHeight / 2 + labelSpaceBottom;
        }
        return height / 2 - barHeight / 2 - labelSpaceTop;
      })
      .attr('fill', 'gray')
      .attr({
        'font-family': 'Roboto, Helvetica Neue, sans-serif',
        'font-size': (d, i) => {
          return i < labelData.length / 2 ? chartData.labelFontSizeBottom + 'px' : chartData.labelFontSizeTop + 'px';
        },
        'font-weight': (d, i) => (i < labelData.length / 2 ? 'normal' : 'bolder')
      })
      .transition()
      .duration(textAnimationDuration)
      .style({
        'fill-opacity': 1
      })
      .text((d, i) => {
        // if the label doesn't have a value return nothing to hide that label
        if (!d.hideLabel) {
          if (i < labelData.length / 2) {
            return d.val;
          } else if (d.val) {
            return d.name;
          }
        }
        return '';
      });
  }

  function mouseoutFn() {
    const labels = svg.selectAll('text').data([]);
    labels
      .exit()
      .transition()
      .duration(textAnimationDuration)
      .style({
        'fill-opacity': 0.0
      })
      .remove();
  }
  if (mode !== 'group' && mode !== 'print') {
    svg.on('mouseenter', mouseoverfn).on('mouseleave', mouseoutFn);
  }
  // always show labels if print is passed in
  if (mode === 'print') {
    mouseoverfn();
  }

  scatterPoints
    .on('click', function handleClick(point) {
      if (typeof scatterClickHandler === 'function') {
        scatterClickHandler(point, d3.select(this), scatterPoints, genderToggle);
      }
    })
    .on('mouseenter', function scatterMouseOverFn(point) {
      if (scatterHoverHandler) {
        scatterHoverHandler(point, d3.select(this), genderToggle);
        return;
      }
      const toolTip = d3
        .select('#' + containerId)
        .select('.d3Tooltip')
        .data([point]);

      toolTip
        .style({
          position: 'absolute',
          visibility: 'visible',
          top: () => {
            const top = height / 2 - toolTipToBarSpacing / 2 - 40;
            return top + 'px';
          },
          left: d => d.x + paddingOffset + 5 + toolTipWidth / 2 + 'px'
        })
        .html(d => {
          let position = 'Center';
          return chartData.tooltipFormatter(d, position);
        });
    })

    .on('mouseleave', function scatterMouseOutFn(point) {
      if (scatterHoverHandlerOut) {
        scatterHoverHandlerOut(point, d3.select(this), genderToggle);
        return;
      }
      const toolTip = d3.select('#' + containerId).select('.d3Tooltip');

      toolTip.style({
        visibility: 'hidden'
      });
    });

  return function setLabelVisibility(isVisible) {
    if (isVisible) {
      mouseoverfn();
    } else {
      mouseoutFn();
    }
  };
}
